/**
 * Ruxsatlar oynasi (faqat owner): filial menejeri va team lead uchun. Rol -- doira (filial yoki jamoa), bu yerda --
 * shu doira ichida nimalar ochiq. Edit oynasidan alohida: shaxs ma'lumoti va ruxsatlar aralashmasin.
 * Saqlash versiya bilan (409 -- eng so'nggi holat ko'rsatiladi); o'zgarish o'sha xodimga darhol yetadi.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { useSaveUserAccess, useUserAccess } from "@/features/admin/api";
import type { AccessItem, AdminUser } from "@/features/admin/types";
import type { Permission } from "@/features/auth/types";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ErrorState, PageLoading } from "@/components/ui/misc";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";

export type AccessTarget = Pick<AdminUser, "id" | "full_name" | "display_name" | "role" | "branch_name">;

const GROUPS: AccessItem["group"][] = ["sections", "drivers", "carriers"];

export function AccessModal({ user, onOpenChange }: { user: AccessTarget | null; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation("admin");
  const { t: tc } = useTranslation();
  const access = useUserAccess(user?.id ?? null);
  const save = useSaveUserAccess();
  // Saqlanmagan o'zgarishlar; null -- serverdagi holat ko'rsatiladi
  const [draft, setDraft] = useState<Partial<Record<Permission, boolean>> | null>(null);
  useEffect(() => setDraft(null), [user?.id]);

  const data = access.data;
  const items = data?.items ?? [];
  const value = (i: AccessItem) => draft?.[i.key] ?? i.value;
  const dirty = items.some((i) => value(i) !== i.value);
  const custom = items.some((i) => value(i) !== i.default);

  function toggle(i: AccessItem, on: boolean) {
    setDraft({ ...Object.fromEntries(items.map((x) => [x.key, value(x)])), [i.key]: on });
  }

  async function submit() {
    if (!user || !data) return;
    try {
      await save.mutateAsync({ id: user.id, values: Object.fromEntries(items.map((i) => [i.key, value(i)])), version: data.version });
      toast.success(t("access.saved", { name: user.display_name }));
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) {
        toast.warning(t("access.conflict"));
        setDraft(null);
        void access.refetch();
      } else {
        toast.error(err instanceof ApiError ? err.message : tc("toast.failed"));
      }
    }
  }

  const scope = user ? [tc(`roles.${user.role}`), user.branch_name].filter(Boolean).join(" · ") : undefined;
  return (
    <Modal
      open={!!user}
      onOpenChange={onOpenChange}
      title={user ? t("access.title", { name: user.full_name }) : ""}
      description={scope}
      footer={
        data?.editable ? (
          <>
            <Button variant="ghost" className="mr-auto" disabled={!custom || save.isPending} onClick={() => setDraft(Object.fromEntries(items.map((i) => [i.key, i.default])))}>
              <RotateCcw size={14} /> {t("access.reset")}
            </Button>
            <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
            <Button variant="primary" loading={save.isPending} disabled={!dirty} onClick={submit}>
              {tc("actions.save")}
            </Button>
          </>
        ) : undefined
      }
    >
      {access.isPending ? (
        <PageLoading />
      ) : access.isError ? (
        <ErrorState message={access.error.message} onRetry={() => access.refetch()} />
      ) : !data?.editable ? (
        <p className="text-[13px] text-ink-soft">{t("access.notEditable")}</p>
      ) : (
        // Past ekranda ro'yxat oyna ichida aylanadi (sarlavha va tugmalar doim ko'rinadi)
        <div className="-mr-2 max-h-[calc(100vh-230px)] space-y-4 overflow-y-auto pr-2">
          <p className="text-[13px] text-ink-soft">{t(`access.intro.${data.role}`)}</p>
          {GROUPS.map((group) => {
            const rows = items.filter((i) => i.group === group);
            if (rows.length === 0) return null;
            return (
              <section key={group}>
                <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-muted uppercase">{t(`access.groups.${group}`)}</h3>
                <div className="divide-y divide-line rounded-[10px] border border-line">
                  {rows.map((i) => {
                    const on = value(i);
                    const label = t(`access.perm.${i.key}.label`);
                    return (
                      <label key={i.key} className="flex cursor-pointer items-start justify-between gap-4 px-3 py-2.5 hover:bg-fill/60">
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-ink">
                            {label}
                            {on !== i.default && <Badge tone="orange">{t("access.custom")}</Badge>}
                          </span>
                          <span className="mt-0.5 block text-[12px] text-muted">{t(`access.perm.${i.key}.desc`)}</span>
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={on}
                          aria-label={label}
                          onClick={() => toggle(i, !on)}
                          className={cn("relative mt-0.5 h-[22px] w-10 shrink-0 rounded-full transition-colors", on ? "bg-primary" : "bg-border")}
                        >
                          <span className={cn("absolute top-[2px] h-[18px] w-[18px] rounded-full bg-surface transition-all", on ? "left-[20px]" : "left-[2px]")} />
                        </button>
                      </label>
                    );
                  })}
                </div>
              </section>
            );
          })}
          <p className="text-[12px] text-muted">{t("access.applyNote")}</p>
        </div>
      )}
    </Modal>
  );
}
